import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Table, FormGroup, Input } from "reactstrap";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl } from "../../../utils/site";

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");
    if (!token) {
      setError("You must be logged in as an admin to view payments.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(getApiUrl("/api/payments"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(response.data || []);
    } catch (requestError) {
      setError("Failed to load payment history.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (paymentId, newStatus) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      await axios.put(
        getApiUrl(`/api/payments/${paymentId}`),
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPayments();
    } catch (requestError) {
      alert("Failed to update payment status. Please try again.");
    }
  };

  const chartData = useMemo(() => {
    const paymentsByDate = payments.reduce((acc, payment) => {
      const date = payment.created_at
        ? new Date(payment.created_at).toLocaleDateString()
        : "Unknown";
      acc[date] = (acc[date] || 0) + Number(payment.amount || 0);
      return acc;
    }, {});

    return Object.entries(paymentsByDate).map(([date, total]) => ({ date, total }));
  }, [payments]);

  return (
    <AdminPageShell
      eyebrow="Revenue control"
      title="Payments"
      description="Track payment volume, gateway activity, and status changes from a more polished finance view."
    >
      <div className="roha-admin-split">
        <AdminSurface title="Payment volume" subtitle="Daily payment totals across recorded transactions">
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(20, 51, 47, 0.12)" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total" fill="#284b46" name="Total amount (ETB)" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </AdminSurface>

        <AdminSurface title="Collection summary" subtitle="Quick financial pulse for the admin team">
          <div className="roha-admin-kpis">
            <div>
              <strong>{payments.length}</strong>
              <span>Total payments</span>
            </div>
            <div>
              <strong>
                ETB {payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0).toFixed(0)}
              </strong>
              <span>Total value</span>
            </div>
            <div>
              <strong>{payments.filter((payment) => payment.status === "success").length}</strong>
              <span>Successful</span>
            </div>
            <div>
              <strong>{payments.filter((payment) => payment.status === "pending").length}</strong>
              <span>Pending</span>
            </div>
          </div>
        </AdminSurface>
      </div>

      <AdminSurface title="Payment records" subtitle="Update status and review payment details">
        {error ? <p className="roha-admin-muted">{error}</p> : null}
        <div className="roha-admin-table-wrap">
          <Table responsive className="roha-admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Order ID</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="roha-admin-empty">
                    Loading payment history...
                  </td>
                </tr>
              ) : payments.length ? (
                payments.map((payment, index) => (
                  <tr key={payment.id}>
                    <td data-label="#">{index + 1}</td>
                    <td data-label="Name">{payment.customer_name || "-"}</td>
                    <td data-label="Phone">{payment.customer_phone || "-"}</td>
                    <td data-label="Order ID">{payment.order_id}</td>
                    <td data-label="Amount">ETB {payment.amount}</td>
                    <td data-label="Method">{payment.payment_gateway || "-"}</td>
                    <td data-label="Status">
                      <FormGroup className="mb-0">
                        <Input
                          type="select"
                          value={payment.status}
                          onChange={(e) => handleUpdateStatus(payment.id, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="success">Success</option>
                          <option value="failed">Failed</option>
                        </Input>
                      </FormGroup>
                    </td>
                    <td data-label="Created At">{payment.created_at ? new Date(payment.created_at).toLocaleString() : "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="roha-admin-empty">
                    No payment records found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </AdminSurface>
    </AdminPageShell>
  );
};

export default PaymentHistory;
